import { ASSETS, LINKS, STEPS, TESTIMONIALS } from './shopData'
import { ShopButton, Reveal } from './ShopUI'

// "Friendly competition for all ages" — the group-page composite (Kitchen
// Crew card with the family photo) on the left, copy on the right over a soft
// lemon wash, mirroring the Shopify original.
export function ShopCommunity() {
  return (
    // No bottom padding on the section — the composite bleeds to its bottom edge.
    <section className="relative overflow-hidden bg-white pt-20 sm:pt-24">
      {/* Lemon gradient wash behind the text column */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(70%_85%_at_100%_50%,rgba(235,255,89,0.55)_0%,rgba(198,255,156,0.25)_45%,rgba(255,255,255,0)_75%)]" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 sm:px-8 md:grid-cols-[1.5fr_1fr] md:gap-16">
        <Reveal className="self-end">
          {/* Composite like the Shopify original: the (tall) group-page card
              cropped to its top, with the family cutout overlapping its
              bottom edge. Slightly oversized beyond its column, spilling left. */}
          <div className="relative overflow-hidden md:-ml-[10%] md:w-[110%]">
            <img
              src={ASSETS.group}
              alt="A dub group page showing the leaderboard for The Kitchen Crew"
              className="block max-h-[600px] w-full object-cover object-top"
            />
            <img
              src={ASSETS.family}
              alt="A family of dub community members celebrating"
              className="absolute bottom-[-5%] left-[-8%] w-[115%] max-w-none"
            />
          </div>
        </Reveal>
        <Reveal delay={100} className="pb-16 md:pb-0">
          {/* Type set inline: the design system's unlayered h2 rule beats
              Tailwind's layered text-* utilities. */}
          <h2
            className="font-[var(--font-heading)] font-extrabold text-[#292237]"
            style={{ fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
          >
            Friendly competition for all ages
          </h2>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-[#66626E]">
            Everyone can be part of the dub club! Join our community of sports fans who enjoy
            predicting winners and competing for the top spot on the leaderboard.
          </p>
        </Reveal>
      </div>
    </section>
  )
}

// "Get the dub" — four numbered steps over the storefront's how-to-play art,
// with the orange bracket graphic.
export function ShopHowItWorks() {
  return (
    <section
      className="relative bg-[#241b33] bg-cover bg-center pt-20 pb-36 sm:pt-24 sm:pb-44"
      style={{ backgroundImage: `url('${ASSETS.bgHowToPlay}')` }}
    >
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 sm:px-8 md:grid-cols-[1fr_1.25fr] md:gap-16">
        <div>
          <Reveal>
            {/* Type + color set inline: the design system's unlayered h2 rule
                (size, and color, which beats text-* utilities on dark bg). */}
            <h2
              className="font-[var(--font-heading)] font-extrabold"
              style={{ color: '#ffffff', fontSize: 'clamp(2.25rem, 3.5vw, 3rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
            >
              Get the dub
            </h2>
          </Reveal>

          <div className="mt-10 space-y-8">
            {STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 90}>
                <div className="flex items-start gap-5">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[linear-gradient(160deg,#c9a5ff_0%,#8b5cf6_100%)] text-lg font-bold text-white shadow-[inset_0_2px_5px_rgba(255,255,255,0.45),0_8px_18px_-8px_rgba(139,92,246,0.8)]">
                    {step.n}
                  </div>
                  <div>
                    <h3
                      className="font-[var(--font-heading)] text-xl font-bold"
                      style={{ color: '#ffffff' }}
                    >
                      {step.title}
                    </h3>
                    <p className="mt-1.5 leading-relaxed text-white/60">{step.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12">
            <ShopButton variant="primary" href={LINKS.register}>
              Create free account
            </ShopButton>
          </Reveal>
        </div>

        <Reveal delay={120} className="flex justify-center md:justify-end">
          {/* Oversized ~20% beyond its column; the extra width spills left
              into the grid gap (the art is a transparent cutout). */}
          <img
            src={ASSETS.bracket}
            alt="Tournament bracket"
            className="w-full drop-shadow-xl md:w-[120%] md:max-w-none"
          />
        </Reveal>
      </div>
    </section>
  )
}

// User reviews: one white card holding both quotes side by side, pulled up so
// it overlaps the bottom of the dark how-it-works section (as on Shopify).
export function ShopTestimonials() {
  return (
    // flow-root stops the card's negative margin collapsing through the
    // section — without it the whole white section shifts up and paints over
    // the dark band instead of the card overlapping it.
    <section className="relative z-10 flow-root bg-white pb-20 sm:pb-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal className="-mt-28">
          <div className="grid gap-12 rounded-[2rem] bg-[linear-gradient(180deg,#d8d4dc_0%,#ffffff_40%)] p-9 shadow-[0_35px_70px_-30px_rgba(41,34,55,0.45)] sm:p-12 md:grid-cols-2 md:gap-16">
            {TESTIMONIALS.map((t) => (
              <figure key={t.location}>
                <img
                  src={t.image}
                  alt={t.name}
                  className="h-14 w-14 rounded-full object-cover"
                />
                <blockquote className="mt-7 text-xl font-bold leading-snug text-[#292237]">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-8">
                  <div className="font-bold text-[#292237]">{t.name}</div>
                  <div className="mt-0.5 text-sm text-[#66626E]">{t.location}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  )
}
